import { useEffect, useMemo, useState } from "react";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { v4 as uuidv4 } from "uuid";
import {
  ABORT_PICKUP,
  CarrierType,
  CREATE_PICKUP,
  displayDeliveryStatusButton,
  downloadBase64File,
  GET_BANK_HOLIDAYS,
  GET_DELIVERY_PROOF,
  GET_FILE_DOWNLOAD_URL,
  hasDeliveryTrackingNumber,
  isPickupRequestAllowedForDoctor,
  OrderStatus,
  TOGGLE_FEATURE,
  GET_ORDER_BY_UNIQUE_ATTRIBUTE,
  UPDATE_ORDER,
  GET_PICKUP_AVAILABILITY,
  ShippingType,
  type File,
  type Order as SharedOrder,
  type OrderComment,
  type Product,
  type Shipping,
  shouldShowRdv2ContinueBanner,
  getExpectedDeliveryDate,
} from "@jasper/shared";
import { useOrderDesignReview, useOrderDesignsList } from "@jasper/use-cases";
import { OrderStatus as GqlOrderStatus } from "/@/generated/graphql";
import { useAuthContext } from "/@/shared/contexts/AuthContext";
import {
  getActivePickups,
  getInitialSelectedProduct,
  getLatestPickupFile,
} from "./order-page.utils";

const HELP_TEMP_ID_STORAGE_KEY = "help_temp_ID";

// The shared GET_ORDER_BY_UNIQUE_ATTRIBUTE document is not typed by codegen:
// the page relies on the shared Order interface, plus the queried `shipping`.
export type OrderPageOrder = SharedOrder & { shipping?: Shipping | null };

export type CreatePickupFormData = {
  clinicClosingTime: string;
  packageReadyTime: string;
  date: string;
  comment: string | null;
};

// Open state of a modal (V1) or of an inline panel (V2).
const useOpenState = () => {
  const [opened, { open, close, toggle }] = useDisclosure(false);
  return { opened, open, close, toggle };
};

export type OpenState = ReturnType<typeof useOpenState>;

export const useOrderPage = (orderId: string) => {
  const { user: connectedUser } = useAuthContext();
  const connectedUserEmail = connectedUser?.email ?? "";

  const [getPresignedDownload] = useLazyQuery(GET_FILE_DOWNLOAD_URL);
  const [createPickupMutation] = useMutation(CREATE_PICKUP);
  const [abortPickupMutation] = useMutation(ABORT_PICKUP);
  const [updateOrder] = useMutation(UPDATE_ORDER);
  const [getDeliveryProofGQL] = useMutation(GET_DELIVERY_PROOF);

  const { orderDesigns } = useOrderDesignsList({ orderId });
  const { openedOrderDesign, requestedOrderDesign, remainingTimeToReview } =
    useOrderDesignReview({ orderId });

  const latestOrderDesign = useMemo(() => {
    if (orderDesigns.length === 0) {
      return null;
    }
    return orderDesigns.reduce((latest, current) =>
      new Date(current.updatedAt) > new Date(latest.updatedAt)
        ? current
        : latest
    );
  }, [orderDesigns]);

  const now = useMemo(() => new Date(), [new Date().getDate()]);
  const { data: bankHolidaysData } = useQuery(GET_BANK_HOLIDAYS, {
    variables: {
      where: {
        OR: [{ beginDate: { gte: now } }, { endDate: { gte: now } }],
      },
      take: 10,
      orderBy: [{ beginDate: "asc" }],
    },
  });
  const { data: pickupAvailabilityData } = useQuery(GET_PICKUP_AVAILABILITY, {
    variables: { orderId },
  });
  const { data, loading, error, refetch } = useQuery(
    GET_ORDER_BY_UNIQUE_ATTRIBUTE,
    {
      fetchPolicy: "network-only",
      variables: { where: { id: orderId } },
    }
  );

  const isPickupFromWebappEnabled =
    useFeatureFlagEnabled("PICKUP_FROM_WEBAPP") ?? false;

  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [orderComments, setOrderComments] = useState<OrderComment[]>([]);

  const filesModal = useOpenState();
  const carrierDeliveryModal = useOpenState();
  const pickupModal = useOpenState();
  const helpRequest = useOpenState();
  const redoRequest = useOpenState();

  const getFileDownloadUrl = async (key: string): Promise<string> => {
    const res = await getPresignedDownload({ variables: { key } });
    return res?.data?.getPresignedFileDownloadUrl;
  };

  const getPresignedDownloadUrl = async (key: string): Promise<void> => {
    window.open(await getFileDownloadUrl(key), "_blank");
  };

  const getFilesWithPath = async (
    comments: OrderComment[]
  ): Promise<OrderComment[]> =>
    Promise.all(
      comments.map(async comment => {
        const files = await Promise.all(
          comment.files.map(async (file: File) => {
            try {
              return { ...file, path: await getFileDownloadUrl(file.key) };
            } catch (error) {
              console.error(
                `Error fetching path for file key: ${file.key}`,
                error
              );
              return file;
            }
          })
        );
        return { ...comment, files };
      })
    );

  useEffect(() => {
    if (!data) {
      return;
    }
    const fetchedOrder: SharedOrder = data.getOrderByUniqueAttribute;
    setSelectedProduct(getInitialSelectedProduct(fetchedOrder));
    getFilesWithPath(fetchedOrder.orderComment ?? []).then(setOrderComments);
  }, [data]);

  const setOrderToDraft = async (): Promise<void> => {
    try {
      await updateOrder({
        variables: {
          where: { id: orderId },
          data: { status: { set: GqlOrderStatus.Draft } },
        },
      });
    } catch (e) {
      console.error(e);
    }
  };

  const openHelpRequest = (): void => {
    helpRequest.open();
    if (!localStorage.getItem(HELP_TEMP_ID_STORAGE_KEY)) {
      localStorage.setItem(HELP_TEMP_ID_STORAGE_KEY, uuidv4());
    }
  };

  const getHelpTempId = (): string =>
    localStorage.getItem(HELP_TEMP_ID_STORAGE_KEY) || "";

  const abortPickup = async (pickupId: string): Promise<void> => {
    try {
      notifications.show({
        title: "Tentative d'annulation du ramassage...",
        color: "blue",
        message: "",
        loading: true,
        id: "abort-pickup-notification",
        autoClose: false,
      });
      await abortPickupMutation({ variables: { pickupId } });
      refetch();
      notifications.hide("abort-pickup-notification");
      notifications.show({
        title: "Ramassage annulé",
        color: "green",
        message: "",
      });
    } catch (e) {
      console.error(e);
      notifications.hide("abort-pickup-notification");
      notifications.show({
        title:
          "Une erreur est survenue lors de la tentative d'annulation du ramassage",
        color: "red",
        message: "",
      });
    }
  };

  const createPickup = async (
    pickupOrderId: string,
    orderReference: string,
    formData: CreatePickupFormData
  ): Promise<void> => {
    try {
      notifications.show({
        title: "Création du ramassage en cours..",
        color: "blue",
        message: "",
        loading: true,
        id: "downloading-pickup-label",
        autoClose: false,
      });
      const req = await createPickupMutation({
        variables: {
          orderId: pickupOrderId,
          clinicClosingTime: formData.clinicClosingTime,
          packageReadyTime: formData.packageReadyTime,
          scheduledDate: formData.date,
          comment: formData.comment,
        },
      });
      downloadBase64File(
        req.data.createPickup,
        `etiquette-de-ramassage-${orderReference}`,
        ".pdf"
      );
      notifications.hide("downloading-pickup-label");
      refetch();
      pickupModal.close();
      notifications.show({
        title: "Étiquette de ramassage téléchargée",
        color: "green",
        message: "",
      });
    } catch (e) {
      console.error(e);
      notifications.hide("downloading-pickup-label");
      notifications.show({
        title: "Il y a eu une erreur dans le téléchargement de l'étiquette",
        color: "red",
        message: "",
      });
    }
  };

  const downloadDeliveryProof = async (
    orderReference: string
  ): Promise<void> => {
    try {
      notifications.show({
        title: "Téléchargement de la preuve de livraison en cours..",
        color: "blue",
        message: "",
        loading: true,
        id: "download-delivery-proof",
        autoClose: false,
      });
      const req = await getDeliveryProofGQL({ variables: { orderId } });
      downloadBase64File(
        req.data.getDeliveryProof,
        `preuve-de-livraison-${orderReference}`
      );
      notifications.hide("download-delivery-proof");
      notifications.show({
        title: "Preuve de livraison téléchargée",
        color: "green",
        message: "",
      });
    } catch (e) {
      console.error(e);
      notifications.hide("download-delivery-proof");
      notifications.show({
        title: "Erreur dans le téléchargement de la preuve de livraison",
        color: "red",
        message: "",
      });
    }
  };

  const downloadFile = async (file: File): Promise<void> => {
    try {
      const a = document.createElement("a");
      a.href = await getFileDownloadUrl(file.key);
      a.click();
    } catch (e) {
      console.error(e);
      notifications.show({
        title: "Erreur lors du téléchargement du fichier",
        color: "red",
        message: "",
      });
    }
  };

  const fetchedOrder: OrderPageOrder | undefined =
    data?.getOrderByUniqueAttribute;
  const order: OrderPageOrder | null =
    fetchedOrder && fetchedOrder.products?.length > 0 ? fetchedOrder : null;

  const activePickups = order ? getActivePickups(order) : [];
  const latestPickupFile = getLatestPickupFile(order?.files ?? []);
  // RDV2 continuation of the order dentures (PAC and / or PAP).
  const showRdv2ContinueBanner = !!order && shouldShowRdv2ContinueBanner(order);
  const hasDeliveryTracking =
    !!order && hasDeliveryTrackingNumber(order.shippings);
  const deliveryCarrier = order?.user?.userGroup?.deliveryCarrier;
  const pickupCarrier = order?.user?.userGroup?.pickupCarrier;
  const isShippingFromWebappEnabled = TOGGLE_FEATURE.SHIPPING_FROM_WEBAPP(
    order?.user?.email ?? ""
  );

  const permissions = {
    canDownloadDeliveryProof:
      !!order &&
      deliveryCarrier === CarrierType.FEDEX_TNT &&
      isShippingFromWebappEnabled &&
      order.status === OrderStatus.DELIVERED &&
      hasDeliveryTracking &&
      order.shipping?.shippingType === ShippingType.DELIVERY,
    canComment: !!order && order.status !== OrderStatus.DELIVERED,
    canRequestRedo: !!order && order.status === OrderStatus.DELIVERED,
    canRequestPickup:
      !!order &&
      isPickupFromWebappEnabled &&
      isPickupRequestAllowedForDoctor(order.products ?? []) &&
      activePickups.length === 0 &&
      order.status === OrderStatus.DELIVERED &&
      !!pickupCarrier &&
      TOGGLE_FEATURE.INTEGRATED_PICKUP_CARRIERS(connectedUserEmail).includes(
        pickupCarrier
      ) &&
      TOGGLE_FEATURE.PICKUP_CARRIER_INTEGRATION_FOR_DENTIST(
        connectedUserEmail
      ) &&
      !showRdv2ContinueBanner,
    canShowCarrierDeliveryDetails:
      !!order &&
      !!deliveryCarrier &&
      displayDeliveryStatusButton(
        deliveryCarrier,
        order.status,
        hasDeliveryTracking
      ) &&
      isShippingFromWebappEnabled,
  };

  return {
    order,
    isLoading: loading && !data,
    hasError: !!error,
    refetch,
    connectedUserEmail,
    bankHolidays: bankHolidaysData?.getBankHoliday ?? [],
    pickupAvailabilities: pickupAvailabilityData?.getPickupAvailability ?? [],
    selectedProduct,
    setSelectedProduct,
    orderComments,
    expectedDeliveryDate: order
      ? getExpectedDeliveryDate(order.deliveryDate, order.createdAt)
      : null,
    showRdv2ContinueBanner,
    design: {
      // Same rule as OrderDesignBanner: an opened design still in its review window.
      isAwaitingDentistReview:
        !!openedOrderDesign && (remainingTimeToReview ?? 0) > 0,
      isReviewPending: !!(openedOrderDesign || requestedOrderDesign),
      latestOrderDesignId: latestOrderDesign?.id ?? null,
    },
    permissions,
    modals: {
      files: filesModal,
      carrierDelivery: carrierDeliveryModal,
      pickup: pickupModal,
    },
    // Help and redo requests: modals in V1, inline panels in V2.
    requests: {
      help: helpRequest,
      redo: redoRequest,
    },
    actions: {
      getPresignedDownloadUrl,
      setOrderToDraft,
      openHelpRequest,
      getHelpTempId,
      abortPickup,
      createPickup,
      downloadDeliveryProof,
      downloadFile,
    },
    pickup: {
      activePickupId: activePickups[0]?.id ?? null,
      latestPickupFile,
    },
  };
};

export type OrderPageState = ReturnType<typeof useOrderPage>;
